import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { ThemeService } from '../../services/theme.service';
import { chartColors, kpiColor } from './chart-theme';
import { FinancialOverviewPoint } from '../../services/apis/dashboard/dashboard.service';

export type FinancialOverviewMetric = 'Revenue' | 'NFI' | 'gross_profit_margin' | 'ebitda_margin' | 'net_profit_margin';

export interface FinancialOverviewSelectionRange {
  startPeriod: string;
  endPeriod: string;
}

export interface FinancialOverviewChartContextMenu {
  dataIndex: number;
  nativeEvent: MouseEvent;
}

export interface FinancialOverviewChartClick {
  dataIndex: number;
  period: 'current' | 'comparison';
}

@Component({
  selector: 'app-financial-overview-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './financial-overview-chart.component.html',
  styles: [`
    .selection-band {
      position: absolute;
      top: 12px;
      bottom: 28px;
      z-index: 2;
      pointer-events: none;
      border: 1px solid color-mix(in srgb, var(--overview-selection-color, #2457D6) 48%, transparent);
      background: color-mix(in srgb, var(--overview-selection-color, #2457D6) 10%, transparent);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
    }

    .selection-handle {
      position: absolute;
      top: 0;
      width: 10px;
      height: 100%;
      border: 0;
      padding: 0;
      background: transparent;
      cursor: ew-resize;
      pointer-events: auto;
    }

    .selection-handle--left {
      left: -5px;
    }

    .selection-handle--right {
      right: -5px;
    }
  `],
})
export class FinancialOverviewChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: FinancialOverviewPoint[] | null = null;
  @Input() comparisonData: FinancialOverviewPoint[] | null = null;
  @Input() metric: FinancialOverviewMetric = 'Revenue';
  @Input() selectionRange: FinancialOverviewSelectionRange | null = null;
  @Output() chartClick = new EventEmitter<FinancialOverviewChartClick>();
  @Output() chartContextMenu = new EventEmitter<FinancialOverviewChartContextMenu>();
  @Output() selectionRangeChange = new EventEmitter<FinancialOverviewSelectionRange>();

  options: EChartsOption | null = null;
  selectionOverlay: { left: number; width: number } | null = null;
  readonly viewportSize = 12;
  viewportStart = 0;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: {
    resize: () => void;
    dispatchAction?: (payload: Record<string, unknown>) => void;
    convertToPixel?: (finder: Record<string, unknown>, value: unknown[]) => number[];
  };
  private resizeObserver?: ResizeObserver;
  private forcedTooltipIndex: number | null = null;
  private dragEdge: 'start' | 'end' | null = null;
  private draftSelectionRange: FinancialOverviewSelectionRange | null = null;
  private lastChartContextMenuAt = 0;
  private lastWheelTime = 0;
  private dataReference: FinancialOverviewPoint[] | null = null;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data !== this.dataReference) {
      this.dataReference = this.data;
      this.viewportStart = 0;
    }
    this.buildOptions();
  }

  get hasScrollableViewport(): boolean {
    return (this.data?.length ?? 0) > this.viewportSize;
  }

  get canMoveOlder(): boolean {
    return this.hasScrollableViewport && this.viewportStart > 0;
  }

  get canMoveNewer(): boolean {
    return this.hasScrollableViewport
      && this.viewportStart < (this.data?.length ?? 0) - this.viewportSize;
  }

  get viewportLabel(): string {
    const rows = this.visibleRows();
    if (rows.length === 0) return '';
    const first = rows[0].periodLabel || rows[0].period;
    const last = rows[rows.length - 1].periodLabel || rows[rows.length - 1].period;
    return `${first} – ${last}`;
  }

  moveViewport(direction: 'older' | 'newer'): void {
    if (!this.hasScrollableViewport) return;
    const maximumStart = Math.max(0, (this.data?.length ?? 0) - this.viewportSize);
    const change = direction === 'newer' ? 1 : -1;
    const nextStart = Math.max(0, Math.min(maximumStart, this.viewportStart + change));
    if (nextStart === this.viewportStart) return;
    this.viewportStart = nextStart;
    this.buildOptions();
  }

  onWheel(event: WheelEvent): void {
    if (!this.hasScrollableViewport || Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
    event.preventDefault();
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastWheelTime < 120) return;
    this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  }

  private readonly onNativeWheel = (event: WheelEvent): void => {
    this.onWheel(event);
  };

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, {
      passive: false,
      capture: true,
    });
    this.resizeObserver = new ResizeObserver(() => {
      this.chartInstance?.resize();
      this.updateSelectionOverlay();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chartContainer?.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
    this.endSelectionResize();
  }

  onChartClick(params: any): void {
    if (typeof params?.dataIndex !== 'number') return;
    this.chartClick.emit({
      dataIndex: params.dataIndex + this.viewportStart,
      period: String(params.seriesName ?? '').includes('Comparison') ? 'comparison' : 'current',
    });
  }

  onChartContextMenu(params: any): void {
    if (typeof params?.dataIndex !== 'number') return;
    const nativeEvent = params.event?.event ?? params.event;
    nativeEvent?.preventDefault?.();
    this.lastChartContextMenuAt = Date.now();
    this.chartContextMenu.emit({ dataIndex: params.dataIndex + this.viewportStart, nativeEvent });
  }

  onContainerContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (Date.now() - this.lastChartContextMenuAt < 80) return;

    const rows = this.data ?? [];
    if (rows.length === 0) return;

    const index = this.nearestPeriodIndex(event.clientX);
    if (index < 0) return;
    this.chartContextMenu.emit({ dataIndex: index + this.viewportStart, nativeEvent: event });
  }

  onChartInit(instance: {
    resize: () => void;
    dispatchAction?: (payload: Record<string, unknown>) => void;
    convertToPixel?: (finder: Record<string, unknown>, value: unknown[]) => number[];
  }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
    this.updateSelectionOverlay();
  }

  beginSelectionResize(edge: 'start' | 'end', event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragEdge = edge;
    this.draftSelectionRange = this.selectionRange;
    window.addEventListener('pointermove', this.onSelectionPointerMove);
    window.addEventListener('pointerup', this.onSelectionPointerUp);
  }

  onPointerMove(event: MouseEvent): void {
    const hitIndex = this.findNearestLineHit(event);
    if (hitIndex === null) {
      if (this.forcedTooltipIndex !== null) {
        this.chartInstance?.dispatchAction?.({ type: 'hideTip' });
        this.forcedTooltipIndex = null;
      }
      return;
    }

    if (hitIndex === this.forcedTooltipIndex) return;
    this.forcedTooltipIndex = hitIndex;
    this.chartInstance?.dispatchAction?.({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: hitIndex,
    });
  }

  onPointerLeave(): void {
    this.forcedTooltipIndex = null;
    this.chartInstance?.dispatchAction?.({ type: 'hideTip' });
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const rows = this.visibleRows();
    const c = chartColors();
    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const periods = rows.map(r => r.periodLabel || r.period);
    const comparisonRows = this.visibleComparisonRows();
    const comparisonPeriods = comparisonRows.map(r => r.periodLabel || r.period);
    const selectedLabel = this.metricLabel();
    const selectedColor = this.selectedMetricColor;
    const selectedMetric = this.selectedMetric();
    const selectedValues = rows.map(r => this.metricValue(r));
    const comparisonValues = comparisonRows.map(row => this.metricValue(row));
    const hasComparison = comparisonValues.length > 0;
    const comparisonColor = document.documentElement.classList.contains('dark') ? '#A3E635' : '#65A30D';
    const currentSeriesColor = selectedColor;
    const currentSeriesName = hasComparison ? `${selectedLabel} · Current` : selectedLabel;
    const comparisonSeriesName = `${selectedLabel} · Comparison`;
    const axisPeriods = periods.map((period, index) =>
      hasComparison && comparisonPeriods[index]
        ? `${period}|||${comparisonPeriods[index]}`
        : period
    );

    this.options = {
      animation: true,
      animationDuration: 140,
      animationDurationUpdate: 120,
      animationEasingUpdate: 'cubicOut',
      grid: { left: 40, right: 18, top: this.hasScrollableViewport ? 44 : 20, bottom: 42, containLabel: true },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'filter',
          zoomOnMouseWheel: false,
          moveOnMouseWheel: false,
        },
      ],
      tooltip: {
        trigger: 'axis',
        triggerOn: 'none',
        confine: true,
        transitionDuration: 0,
        axisPointer: { type: 'none' },
        backgroundColor: c.cardBg,
        borderColor: c.cardBorder,
        padding: [12, 16],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 10px 30px rgba(0,0,0,0.16); border-radius: 8px;',
        formatter: (params: any) => {
          if (!params || params.length === 0) return '';
          const items = Array.isArray(params) ? params : [params];
          const rawTitle = String(items[0]?.name ?? '');
          const [currentPeriod, comparisonPeriod] = rawTitle.split('|||');
          const title = comparisonPeriod
            ? `Current: ${currentPeriod}<br>Comparison: ${comparisonPeriod}`
            : currentPeriod;
          const rowsHtml = items
            .filter((item: any) => item.value !== undefined)
            .map((item: any) => {
              const value = Number(item.value ?? 0);
              const display = item.seriesName?.includes('%')
                ? `${value.toFixed(1)}%`
                : new Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(value);

              return `<div style="display:flex; align-items:center; justify-content:space-between; gap:24px; margin-bottom:4px;">
                <div style="display:flex; align-items:center; gap:8px;">
                  <span style="display:inline-block; width:10px; height:10px; border-radius:50%; background-color:${item.color};"></span>
                  <span style="color:${c.textSecondary}; font-size:13px; font-weight:600;">${item.seriesName}</span>
                </div>
                <span style="color:${c.textPrimary}; font-size:13px; font-weight:800;">${display}</span>
              </div>`;
            })
            .join('');

          return `<div class="select-none" style="font-family: Inter, sans-serif;">
            <p style="margin:0 0 8px 0; color:${c.textSecondary}; font-size:12px; font-weight:700;">${title}</p>
            ${rowsHtml}
          </div>`;
        },
      },
      legend: {
        data: [
          { name: currentSeriesName, icon: 'circle', itemStyle: { color: currentSeriesColor } },
          ...(hasComparison ? [{ name: comparisonSeriesName, icon: 'circle', itemStyle: { color: comparisonColor } }] : []),
        ],
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 500 },
        bottom: 0,
        itemGap: 12,
      },
      xAxis: {
        type: 'category',
        data: axisPeriods,
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 10,
          lineHeight: 12,
          margin: 12,
          interval: 0,
          hideOverlap: false,
          formatter: (value: string) => {
            const compactPeriod = (period: string) => {
              const match = period.trim().match(/^([A-Za-z]{3,9})\s+(\d{4})$/);
              return match ? `${match[1].slice(0, 3)} ${match[2]}` : period;
            };
            const [currentPeriod, comparisonPeriod] = value.split('|||');
            if (!comparisonPeriod) return compactPeriod(currentPeriod);
            return `{current|Cur: ${compactPeriod(currentPeriod)}}\n{comparison|Cmp: ${compactPeriod(comparisonPeriod)}}`;
          },
          rich: {
            current: { color: selectedColor, fontSize: 10, fontWeight: 700, lineHeight: 14 },
            comparison: { color: comparisonColor, fontSize: 10, fontWeight: 700, lineHeight: 14 },
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            color: c.textSecondary,
            fontSize: 11,
            formatter: (value: number) => {
              if (selectedMetric.isPercent) return `${value.toFixed(0)}%`;
              if (value === 0) return '0';
              const sign = value < 0 ? '-' : '';
              const abs = Math.abs(value);
              if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(0)}k`;
              return `${value}`;
            },
          },
          splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed' } },
        },
      ],
      series: [
        {
          name: currentSeriesName,
          type: rows.length === 1 ? 'bar' : 'line',
          data: selectedValues,
          barMaxWidth: 56,
          smooth: 0.2,
          triggerLineEvent: true,
          showAllSymbol: true,
          clip: false,
          showSymbol: true,
          symbolSize: rows.length <= 4 ? 11 : 8,
          itemStyle: {
            color: currentSeriesColor,
            borderRadius: rows.length === 1
              ? (selectedValues[0] < 0 ? [0, 0, 14, 14] : [14, 14, 0, 0])
              : 0,
          },
          lineStyle: { color: currentSeriesColor, width: 3 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.toRgba(selectedColor, 0.28) },
                { offset: 1, color: this.toRgba(selectedColor, 0) },
              ],
            },
          },
          emphasis: { focus: 'series' as const },
        },
        ...(hasComparison ? [{
          name: comparisonSeriesName,
          type: comparisonValues.length === 1 ? 'bar' as const : 'line' as const,
          data: comparisonValues,
          barMaxWidth: 56,
          smooth: 0.2,
          showAllSymbol: true,
          showSymbol: true,
          symbol: 'diamond',
          symbolSize: comparisonValues.length <= 4 ? 11 : 8,
          itemStyle: {
            color: comparisonColor,
            borderRadius: comparisonValues.length === 1
              ? (comparisonValues[0] < 0 ? [0, 0, 14, 14] : [14, 14, 0, 0])
              : 0,
          },
          lineStyle: { color: comparisonColor, width: 3, type: 'dashed' as const },
          emphasis: { focus: 'series' as const },
        }] : []),
      ],
    };

    queueMicrotask(() => this.updateSelectionOverlay());
  }

  private readonly onSelectionPointerMove = (event: PointerEvent) => {
    if (!this.dragEdge || !this.draftSelectionRange) return;
    const period = this.periodForPointer(event.clientX);
    if (!period) return;

    this.draftSelectionRange = this.normalizeSelectionRange({
      ...this.draftSelectionRange,
      [this.dragEdge === 'start' ? 'startPeriod' : 'endPeriod']: period,
    });
    this.updateSelectionOverlay();
  };

  private readonly onSelectionPointerUp = () => {
    if (this.draftSelectionRange) {
      this.selectionRangeChange.emit(this.draftSelectionRange);
    }
    this.endSelectionResize();
  };

  private endSelectionResize(): void {
    window.removeEventListener('pointermove', this.onSelectionPointerMove);
    window.removeEventListener('pointerup', this.onSelectionPointerUp);
    this.dragEdge = null;
    this.draftSelectionRange = null;
  }

  private updateSelectionOverlay(): void {
    const range = this.draftSelectionRange ?? this.selectionRange;
    const rows = this.visibleRows();
    if (!range || rows.length === 0 || !this.chartInstance?.convertToPixel || !this.chartContainer?.nativeElement) {
      this.selectionOverlay = null;
      return;
    }

    const centers = this.xAxisCenters();
    if (centers.length === 0) {
      this.selectionOverlay = null;
      return;
    }

    const startKey = this.periodKey(range.startPeriod);
    const endKey = this.periodKey(range.endPeriod);
    const startIndex = rows.findIndex(row => this.periodKey(row.period) === startKey);
    const endIndex = rows.findIndex(row => this.periodKey(row.period) === endKey);
    if (startIndex < 0 || endIndex < 0) {
      this.selectionOverlay = null;
      return;
    }

    const leftIndex = Math.min(startIndex, endIndex);
    const rightIndex = Math.max(startIndex, endIndex);
    const left = this.leftBoundaryForIndex(centers, leftIndex);
    const right = this.rightBoundaryForIndex(centers, rightIndex);
    this.selectionOverlay = { left, width: Math.max(8, right - left) };
  }

  private xAxisCenters(): number[] {
    const rows = this.visibleRows();
    if (!this.chartInstance?.convertToPixel) return [];
    return rows
      .map((_, index) => this.chartInstance?.convertToPixel?.({ xAxisIndex: 0 }, [index, 0])?.[0])
      .filter((value): value is number => Number.isFinite(value));
  }

  private leftBoundaryForIndex(centers: number[], index: number): number {
    if (centers.length === 1) return Math.max(0, centers[0] - 28);
    if (index === 0) return centers[0] - ((centers[1] - centers[0]) / 2);
    return (centers[index - 1] + centers[index]) / 2;
  }

  private rightBoundaryForIndex(centers: number[], index: number): number {
    if (centers.length === 1) return centers[0] + 28;
    if (index === centers.length - 1) return centers[index] + ((centers[index] - centers[index - 1]) / 2);
    return (centers[index] + centers[index + 1]) / 2;
  }

  private periodForPointer(clientX: number): string | null {
    if (!this.chartContainer?.nativeElement) return null;
    const rows = this.visibleRows();
    const centers = this.xAxisCenters();
    if (rows.length === 0 || centers.length !== rows.length) return null;

    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    const x = clientX - rect.left;
    const closestIndex = this.nearestCenterIndex(x, centers);

    return rows[closestIndex]?.period ?? null;
  }

  private nearestPeriodIndex(clientX: number): number {
    if (!this.chartContainer?.nativeElement) return -1;
    const centers = this.xAxisCenters();
    if (centers.length === 0) return -1;
    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    return this.nearestCenterIndex(clientX - rect.left, centers);
  }

  private nearestCenterIndex(x: number, centers: number[]): number {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    centers.forEach((center, index) => {
      const distance = Math.abs(center - x);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  private normalizeSelectionRange(range: FinancialOverviewSelectionRange): FinancialOverviewSelectionRange {
    const rows = this.data ?? [];
    const startKey = this.periodKey(range.startPeriod);
    const endKey = this.periodKey(range.endPeriod);
    const startIndex = rows.findIndex(row => this.periodKey(row.period) === startKey);
    const endIndex = rows.findIndex(row => this.periodKey(row.period) === endKey);
    if (startIndex < 0 || endIndex < 0 || startIndex <= endIndex) return range;
    return { startPeriod: range.endPeriod, endPeriod: range.startPeriod };
  }

  private periodKey(period: string): string {
    return period.match(/^(\d{4}-\d{2})/)?.[1] ?? period;
  }

  private findNearestLineHit(event: MouseEvent): number | null {
    if (!this.chartContainer?.nativeElement || !this.chartInstance?.convertToPixel) return null;

    const rows = this.visibleRows();
    if (rows.length === 0) return null;

    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    const pointer = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    if (rows.length === 1) {
      const currentValue = this.metricValue(rows[0]);
      const values = [currentValue, ...this.visibleComparisonRows().slice(0, 1).map(row => this.metricValue(row))];
      const zeroPixel = this.chartInstance.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [0, 0]);
      const valuePixels = values.map(value =>
        this.chartInstance?.convertToPixel?.({ xAxisIndex: 0, yAxisIndex: 0 }, [0, value])
      );
      const barHalfWidth = values.length > 1 ? 64 : 34;
      const isInsideBar = valuePixels.some(pixel => {
        if (!pixel || !zeroPixel || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) return false;
        const top = Math.min(zeroPixel[1], pixel[1]);
        const bottom = Math.max(zeroPixel[1], pixel[1]);
        return Math.abs(pointer.x - pixel[0]) <= barHalfWidth && pointer.y >= top && pointer.y <= bottom;
      });
      if (isInsideBar) return 0;
    }

    let points: Array<{ x: number; y: number; index: number }> = [];
    try {
      const addPoints = (values: number[], yAxisIndex: number) => {
        values.forEach((value, index) => {
          const pixel = this.chartInstance?.convertToPixel?.(
            { xAxisIndex: 0, yAxisIndex },
            [index, value]
          );
          if (!pixel || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) return;
          points.push({ x: pixel[0], y: pixel[1], index });
        });
      };

      addPoints(rows.map(row => this.metricValue(row)), 0);
    } catch {
      return null;
    }

    if (points.length === 0) return null;

    const pointHitRadius = rows.length <= 4 ? 26 : 18;
    const lineHitRadius = rows.length <= 4 ? 22 : 14;
    let closestIndex: number | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const point of points) {
      const distance = this.distance(pointer.x, pointer.y, point.x, point.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = point.index;
      }
    }

    for (let index = 0; index < points.length - 1; index += 1) {
      const start = points[index];
      const end = points[index + 1];
      const { distance, t } = this.distanceToSegment(pointer.x, pointer.y, start.x, start.y, end.x, end.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = t < 0.5 ? start.index : end.index;
      }
    }

    const threshold = points.length === 1 ? pointHitRadius : lineHitRadius;
    return closestDistance <= threshold ? closestIndex : null;
  }

  private distance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.hypot(x1 - x2, y1 - y2);
  }

  private distanceToSegment(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): { distance: number; t: number } {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      return { distance: this.distance(px, py, x1, y1), t: 0 };
    }

    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
    const x = x1 + t * dx;
    const y = y1 + t * dy;
    return { distance: this.distance(px, py, x, y), t };
  }

  private selectedMetric(): { isPercent: boolean } {
    return { isPercent: this.metric !== 'Revenue' && this.metric !== 'NFI' };
  }

  private metricLabel(): string {
    if (this.metric === 'NFI') return 'Net Fee Income';
    if (this.metric === 'gross_profit_margin') return 'Gross Profit Margin %';
    if (this.metric === 'ebitda_margin') return 'EBITDA Margin %';
    if (this.metric === 'net_profit_margin') return 'Net Profit Margin %';
    return 'Revenue';
  }

  get selectedMetricColor(): string {
    if (this.metric !== 'Revenue') return kpiColor(this.metric);
    const c = chartColors();
    return c.primary || c.chartSeries1 || '#1AAE9F';
  }

  private metricValue(row: FinancialOverviewPoint): number {
    if (this.metric === 'NFI') return Number(row.nfi ?? 0);
    if (this.metric === 'gross_profit_margin') return Number(row.grossProfitMargin ?? 0);
    if (this.metric === 'ebitda_margin') return Number(row.ebitdaMargin ?? 0);
    if (this.metric === 'net_profit_margin') return Number(row.netProfitMargin ?? 0);
    return Number(row.totalIncome ?? 0);
  }

  private visibleRows(): FinancialOverviewPoint[] {
    const rows = this.data ?? [];
    return this.hasScrollableViewport
      ? rows.slice(this.viewportStart, this.viewportStart + this.viewportSize)
      : rows;
  }

  private visibleComparisonRows(): FinancialOverviewPoint[] {
    const rows = this.comparisonData ?? [];
    return this.hasScrollableViewport
      ? rows.slice(this.viewportStart, this.viewportStart + this.viewportSize)
      : rows;
  }

  private toRgba(color: string, alpha: number): string {
    const hex = color.trim();
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return color;
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
